import React, { useState } from 'react';
import { isWeekend, isHoliday, getDayName, formatDate, formatDateLong, formatCurrency } from '../utils/holidays';
import * as XLSX from 'xlsx';

function HomeWork({ month, year, registrations, userSettings, userProfile, afasSubmissions, setAfasSubmissions }) {
  const [copyToast, setCopyToast] = useState(false);
  const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
  const isSubmitted = afasSubmissions[monthKey]?.thuiswerk || false;
  const submittedDate = afasSubmissions[monthKey]?.thuiswerkDatum || null;

  const handleAfasToggle = () => {
    setAfasSubmissions(prev => ({
      ...prev,
      [monthKey]: {
        ...prev[monthKey],
        thuiswerk: !isSubmitted,
        thuiswerkDatum: !isSubmitted ? new Date().toLocaleDateString('nl-NL') : null
      }
    }));
  };

  // Get all home work days in the month
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthRegistrations = registrations[monthKey] || {};
  const homeWorkDays = [];

  const werkdagen = userSettings.werkdagen ?? [1, 2, 3, 4, 5];
  const defaultThuiswerkDagen = userSettings.defaultThuiswerkDagen || [];

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(Date.UTC(year, month, day));
    const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const isWerkdag = werkdagen.includes(date.getDay());
    const isAutoVrij = isWeekend(date) || isHoliday(date, year) || !isWerkdag;
    const defaultDag = (!isAutoVrij && defaultThuiswerkDagen.includes(date.getDay())) ? 'thuis' : '';
    const registration = monthRegistrations[dateString] ?? (isAutoVrij ? 'vrij' : defaultDag);

    if (registration === 'thuis' && !isWeekend(date) && !isHoliday(date, year)) {
      homeWorkDays.push({
        date,
        dateString,
        formattedDate: formatDate(date),
        dayName: getDayName(date)
      });
    }
  }

  const totalDays = homeWorkDays.length;
  const totalCompensation = totalDays * userSettings.thuiswerkVergoeding;
  const volledigeNaam = [userProfile?.voornaam, userProfile?.achternaam].filter(Boolean).join(' ') || '—';

  const downloadExcel = () => {
    const maandNaam = new Date(year, month).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
    const rows = [
      [`Thuiswerkdagen - ${maandNaam}`],
      [],
      ['Aantal thuiswerkdagen', totalDays],
      ['Vergoeding per dag', userSettings.thuiswerkVergoeding],
      ['Totaal vergoeding', totalCompensation],
      [],
      ['Datum', 'Dag', 'Eenheid', 'Vergoeding'],
      ...homeWorkDays.map(day => [day.formattedDate, day.dayName, 'thuiswerkdag', userSettings.thuiswerkVergoeding]),
      ['Totaal', '', '', totalCompensation],
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 14 }, { wch: 12 }, { wch: 16 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Thuiswerkdagen');
    XLSX.writeFile(wb, `Thuiswerkdagen_${maandNaam.replace(' ', '_')}.xlsx`);
  };

  const showCopyToast = () => { setCopyToast(true); setTimeout(() => setCopyToast(false), 3000); };

  const copyToClipboard = () => {
    const maandNaamRaw = new Date(year, month).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
    const maandNaam = maandNaamRaw.charAt(0).toUpperCase() + maandNaamRaw.slice(1);
    const text = `Thuiswerkdagen ${maandNaam}
Aantal thuiswerkdagen: ${totalDays}

${homeWorkDays.map(day => `${day.dayName} ${formatDateLong(day.date)}`).join('\n')}`;

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(showCopyToast);
    } else {
      const el = document.createElement('textarea');
      el.value = text;
      el.style.position = 'fixed';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.focus();
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      showCopyToast();
    }
  };

  return (
    <>
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">
            Thuiswerkdagen - {new Date(year, month).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })}
          </h2>
          <div className="flex items-center space-x-2">
            {isSubmitted && (
              <span className="text-xs text-green-700">Ingediend op {submittedDate}</span>
            )}
            <button
              onClick={handleAfasToggle}
              className={`px-4 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                isSubmitted
                  ? 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-400'
              }`}
            >
              {isSubmitted ? '✓ Ingediend in AFAS' : 'Ingediend in AFAS'}
            </button>
            <button
              onClick={copyToClipboard}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm"
            >
              Kopie AFAS
            </button>
          </div>
        </div>

        <div className="mb-6 flex gap-3">
          <div className="bg-blue-50 rounded-lg p-4 flex-1">
            <p className="text-xs text-blue-600 mb-1">Vergoeding per dag</p>
            <p className="text-sm font-semibold text-blue-900">{formatCurrency(userSettings.thuiswerkVergoeding)}</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 flex-1">
            <p className="text-xs text-blue-600 mb-1">Aantal thuisdagen</p>
            <p className="text-sm font-semibold text-blue-900">{totalDays}</p>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4 flex-1">
            <p className="text-xs text-yellow-700 mb-1">Totaal vergoeding</p>
            <p className="text-sm font-semibold text-yellow-900">{formatCurrency(totalCompensation)}</p>
          </div>
        </div>

        {homeWorkDays.length > 0 && (
          <div className="flex justify-end mb-4 pb-2.5 border-b border-gray-200">
            <button onClick={downloadExcel} className="flex items-center gap-1 text-xs text-green-700 hover:text-green-800 transition">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download Excel
            </button>
          </div>
        )}
        {homeWorkDays.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Datum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Eenheid
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vergoeding
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {homeWorkDays.map((day, index) => (
                  <tr key={day.dateString}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {day.dayName} {day.formattedDate}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      thuiswerkdag
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatCurrency(userSettings.thuiswerkVergoeding)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-medium">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" colSpan="2">Totaal</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(totalCompensation)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">
            Geen thuiswerkdagen geregistreerd voor deze maand.
          </p>
        )}
      </div>
    </div>

    {copyToast && (
      <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-green-600 text-white px-5 py-3 rounded-xl shadow-xl animate-fade-in">
        <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
        <span className="font-medium">Gekopieerd naar klembord</span>
      </div>
    )}
    </>
  );
}

export default HomeWork;